/**
 * Node Service
 */

import { isAxiosError } from 'axios';
import { httpClient } from '../http-client';
import type { NodeData, NodeOrigin } from '../../engine/types';

const API_BASE_URL = '/api/v1';

export const NodeService = {
  /**
   */
  async exploreSubKnowledge(parentId: string, keyword: string): Promise<NodeData> {
    try {
      const url = `${API_BASE_URL}/topic-nodes/${parentId}/explore-sub-knowledge`;

      const response = await httpClient.post<{
        id: string;
        title: string;
        description?: string;
        keywords?: string[];
        nodeOrigin?: string;
      }>(url, { keyword });
      const topicNode = response.data;

      return {
        id: topicNode.id,
        type: 'topic' as const,
        title: topicNode.title,
        description: topicNode.description || '',
        contents: '',
        tags: topicNode.keywords || [],
        nodeOrigin: (topicNode.nodeOrigin === 'system' ? 'system' : 'user') satisfies NodeOrigin
      };
    } catch (error) {
      console.error('Failed to explore sub-knowledge:', error);
      throw error;
    }
  },

  /**
   * Delete a topic node
   */
  async deleteTopicNode(nodeId: string): Promise<void> {
    try {
      const url = `${API_BASE_URL}/content/nodes/${nodeId}`;
      await httpClient.delete<void>(url);
    } catch (error) {
      console.error('Error deleting topic node:', error);
      throw error;
    }
  },

  /**
   */
  async updateNodePositions(
    treeId: string,
    positions: Array<{ nodeId: string; x: number; y: number; width?: number; height?: number }>
  ): Promise<{ message: string; updatedCount: number }> {
    try {
      const url = `${API_BASE_URL}/learning-trees/${treeId}/nodes/positions`;
      const response = await httpClient.patch<{ message: string; updatedCount: number }>(url, { positions });
      return response.data;
    } catch (error) {
      console.error('Error updating node positions:', error);
      throw error;
    }
  },

  /**
   */
  async resetNodePositions(
    treeId: string
  ): Promise<{ message: string; resetCount: number }> {
    try {
      const url = `${API_BASE_URL}/learning-trees/${treeId}/nodes/positions`;
      const response = await httpClient.delete<{ message: string; resetCount: number }>(url);
      return response.data;
    } catch (error) {
      console.error('Error resetting node positions:', error);
      throw error;
    }
  },

  /**
   */
  async detachStepNode(
    treeId: string,
    nodeId: string
  ): Promise<{ message: string }> {
    try {
      const url = `${API_BASE_URL}/learning-trees/${treeId}/nodes/${nodeId}/detach`;
      const response = await httpClient.post<{ message: string }>(url);
      return response.data;
    } catch (error) {
      console.error('Error detaching step node:', error);
      throw error;
    }
  },

  /**
   */
  async attachStepNode(
    treeId: string,
    nodeId: string,
    sourceNodeId: string,
    targetNodeId: string
  ): Promise<{ message: string }> {
    try {
      const url = `${API_BASE_URL}/learning-trees/${treeId}/nodes/${nodeId}/attach`;
      const response = await httpClient.post<{ message: string }>(url, { sourceNodeId, targetNodeId });
      return response.data;
    } catch (error) {
      console.error('Error attaching step node:', error);
      throw error;
    }
  },

  /**
   */
  async changeNodeParent(
    treeId: string,
    nodeId: string,
    newParentId: string
  ): Promise<{ message: string; nodeId: string; oldParentId: string | null; newParentId: string }> {
    try {
      const url = `${API_BASE_URL}/learning-trees/${treeId}/nodes/${nodeId}/change-parent`;
      const response = await httpClient.post<{ message: string; nodeId: string; oldParentId: string | null; newParentId: string }>(url, { newParentId });
      return response.data;
    } catch (error) {
      console.error('Error changing node parent:', error);
      throw error;
    }
  },

  /**
   */
  async promoteToIndependent(
    treeId: string,
    nodeId: string
  ): Promise<{ message: string; nodeId: string; oldDepth: number; oldParentId: string | null }> {
    try {
      const url = `${API_BASE_URL}/learning-trees/${treeId}/nodes/${nodeId}/promote-to-independent`;
      const response = await httpClient.post<{ message: string; nodeId: string; oldDepth: number; oldParentId: string | null }>(url);
      return response.data;
    } catch (error) {
      console.error('Error promoting node to independent:', error);
      throw error;
    }
  },

async createChildFromSelection(
    treeId: string,
    parentNodeId: string,
    selectedText: string,
    userIntent?: string,
    contentStyle?: string
  ): Promise<CreateChildNodeResult> {
    try {
      const url = `${API_BASE_URL}/learning-trees/${treeId}/nodes/${parentNodeId}/create-child/sync`;
      const response = await httpClient.post<CreateChildNodeResult>(url, {
        selectedText,
        userIntent,
        contentStyle,
      });
      return response.data;
    } catch (error: unknown) {
      if (isAxiosError(error) && error.response?.status === 400 && error.response?.data) {
        const errorData = error.response.data as { message?: string; error?: string };
        return {
          success: false,
          isValid: false,
          reason: errorData.message || errorData.error || 'The selected text is not suitable.',
        };
      }
      console.error('Error creating child node from selection:', error);
      throw error;
    }
  },

  /**
   */
  async createChildFromSelectionAsync(
    treeId: string,
    parentNodeId: string,
    selectedText: string
  ): Promise<{ workflowId: string; runId: string; message: string }> {
    try {
      const url = `${API_BASE_URL}/learning-trees/${treeId}/nodes/${parentNodeId}/create-child`;
      const response = await httpClient.post<{ workflowId: string; runId: string; message: string }>(url, { selectedText });
      return response.data;
    } catch (error) {
      console.error('Error creating child node (async):', error);
      throw error;
    }
  },

  /**
   */
  async getCreateChildNodeStatus(
    treeId: string,
    parentNodeId: string,
    workflowId: string
  ): Promise<CreateChildNodeStatusResult> {
    try {
      const url = `${API_BASE_URL}/learning-trees/${treeId}/nodes/${parentNodeId}/create-child/${workflowId}/status`;
      const response = await httpClient.get<CreateChildNodeStatusResult>(url);
      return response.data;
    } catch (error) {
      console.error('Error getting create child node status:', error);
      throw error;
    }
  },

  /**
   */
  async getStreamingContent(nodeId: string): Promise<StreamingContentResult> {
    try {
      const url = `${API_BASE_URL}/topic-nodes/${nodeId}/streaming-content`;
      const response = await httpClient.get<StreamingContentResult>(url);
      return response.data;
    } catch (error) {
      console.error('Error getting streaming content:', error);
      throw error;
    }
  },
};

// ===== Types =====

export interface CreateChildNodeResult {
  success: boolean;
  isValid: boolean;
  nodeId?: string;
  provisionalTitle?: string;
  finalTitle?: string;
  description?: string;
  keywords?: string[];
  contentGenerationStarted?: boolean;
  reason?: string;
}

export interface CreateChildNodeStatusResult {
  workflowId: string;
  status: 'RUNNING' | 'COMPLETED' | 'FAILED';
  result?: CreateChildNodeResult;
  error?: string;
}

export interface StreamingContentSection {
  title: string;
  role: string;
  estimatedTokens: number;
}

export interface StreamingCompletedSection {
  index: number;
  title: string;
  content: string;
  wordCount?: number;
}

export interface StreamingContentResult {
  status: 'pending' | 'generating' | 'streaming' | 'completed' | 'failed';
  progress: number;
  planning: {
    summary: string;
    sections: StreamingContentSection[];
  } | null;
  completedSections: StreamingCompletedSection[];
  totalSections: number;
  streamingSections: number;
}
